import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { 
  DeviceMobileSpeaker, 
  Handshake, 
  Medal, 
  CurrencyEur 
} from "@phosphor-icons/react";

const values = [
  {
    icon: DeviceMobileSpeaker,
    title: "Réactivité",
    description: "Réponse et traitement rapide."
  },
  {
    icon: Handshake,
    title: "Accompagnement + Formation",
    description: "Votre projet est notre projet. Nous vous accompagnons du début à la fin pour une mise en place sereine."
  },
  {
    icon: Medal,
    title: "Performance",
    description: "Expérimenté, nos solutions sont fiables, efficaces et répondent à vos problématiques."
  },
  {
    icon: CurrencyEur,
    title: "Prix Dédié À L'Éducation Nationale",
    description: "Recherche permanente de votre solution idéal au meilleur prix."
  }
];

export default function QuiSommesNous() {
  return (
    <div className="min-h-screen" data-testid="qui-sommes-nous-page">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-white py-12">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-blue-900 mb-8"
            >
              Qui nous sommes
            </motion.h1>
            
            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="relative w-full rounded-xl overflow-hidden shadow-xl mb-8"
            >
              <img 
                src="https://images.pexels.com/photos/7092613/pexels-photo-7092613.jpeg?auto=compress&cs=tinysrgb&w=1200" 
                alt="Université moderne"
                className="w-full h-[400px] object-cover"
              />
            </motion.div>
            
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-blue-600 text-center"
            >
              Notre société est dédiée exclusivement à l'Education Nationale!
            </motion.h2>
          </div>
        </section>

        {/* About Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Text Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <p className="text-slate-700 leading-relaxed">
                  E@tech est une entreprise privée de support et de services informatiques fondée en 2012.
                  Spécialisé pour l'éducation, nous sommes une équipe solide de professionnels de l'informatique expérimentés, déterminés à stimuler vos projets grâce à des solutions performantes et innovantes.
                </p>
                
                <p className="text-slate-700 leading-relaxed">
                  L'expérience a modelé notre savoir-faire, l'impératif de résultat a aiguisé nos compétences.
                  Nous ajuster aux exigences plurielles d'un projet singulier issu d'un secteur déterminé, telle est notre ambition, notre force, notre différence.
                </p>

                <p className="text-slate-700 leading-relaxed">
                  La société e@Tech a su s'adapter à des attentes clairement différenciées.
                  Son objectif demeure intact : satisfaire les demandes de chacun en considérant les spécificités propres à chaque projet et à chaque établissement.
                </p>

                <p className="text-slate-700 leading-relaxed">
                  <strong>Notre particularité :</strong> Une organisation S.A.V réellement performante et financièrement motivante pour tous nos partenaires « éducations » grâce à la délégation de maintenance qui peut être mis en place selon une convention signée.
                </p>

                <p className="text-slate-700 leading-relaxed">
                  Notre mission ne s'arrête cependant pas à ce seul principe. Au-delà du développement, nous nous attachons à exercer une véritable force de proposition. Être à vos côtés, vous conseiller, c'est aussi notre métier !
                </p>
              </motion.div>

              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <img 
                  src="https://images.unsplash.com/photo-1758270705290-62b6294dd044?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800" 
                  alt="Étudiants adultes en formation"
                  className="rounded-xl shadow-xl w-full h-auto"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-sky-50/50">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-blue-600 text-center mb-16"
            >
              Nos valeurs
            </motion.h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-sky-100/50 rounded-2xl p-8 text-center"
                  data-testid={`value-card-${index}`}
                >
                  <div className="flex justify-center mb-6">
                    <value.icon size={48} weight="duotone" className="text-sky-500" />
                  </div>
                  <h3 className="font-['Outfit'] text-xl font-semibold text-sky-600 italic mb-4">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* References Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-sky-500 text-center mb-16"
            >
              Nos références
            </motion.h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Text Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <div className="space-y-4 text-slate-700">
                  <p>
                    – Fourniture et déploiement de plus de <strong className="text-sky-600">4 450</strong> configurations PC dans les établissements scolaires dans la région Grand EST et le reste de la France sur les trois dernières années en PC bureautique et station CAO, ordinateurs portables, traceurs et imprimantes 3D.
                  </p>
                  <p>
                    – Fourniture et installation de matériel d'infrastructure informatique, de stockage de données et de sauvegarde dans ces mêmes établissements scolaires.
                  </p>
                  <p>
                    – Fourniture de solution multimédia et audiovisuelle (vidéo interactif- visio conférence).
                  </p>
                </div>

                <div className="pt-6 space-y-4 text-slate-700">
                  <p>
                    <strong className="text-sky-600">LYCÉE, GRETA</strong> et <strong className="text-sky-600">CFA</strong> : Notre société est dédiée exclusivement à l'Education Nationale
                  </p>
                  <p>– Intégration globale du système d'information.</p>
                  <p>– Équipement de sites en serveurs et stations de travail (une centaine de postes par site).</p>
                  <p>– Connexions multi sites sur une architecture MICROSOFT Serveur et Terminal Serveur.</p>
                  <p><u>Nature de l'ouvrage</u> : Intégration des applicatifs pédagogiques et de gestion.</p>
                  <p><u>Réalisation</u> : Intégration et migration du système d'information.</p>
                  <p>Autant de clients actifs pour lesquels nous créons, intégrons et suivons leurs systèmes d'information.</p>
                </div>
              </motion.div>

              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <img 
                  src="https://images.unsplash.com/photo-1772971919691-598c37fe4b81?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800" 
                  alt="Étudiant adulte sur ordinateur"
                  className="rounded-xl shadow-xl w-full h-auto"
                />
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
